import React, { useState, useCallback, useEffect } from 'react';
import { Bug, X, Send, Camera, Loader2, CheckCircle, AlertTriangle } from 'lucide-react';
import api from '../utils/api';

export default function BugReportButton() {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', severity: 'medium', user_email: '' });
  const [screenshot, setScreenshot] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const [chatActive, setChatActive] = useState(false);

  useEffect(() => {
    const check = () => setChatActive(!!document.querySelector('[data-testid="chat-interface"]'));
    check();
    const observer = new MutationObserver(check);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  const captureScreenshot = useCallback(async () => {
    setCapturing(true);
    try {
      const html2canvas = (await import('html2canvas')).default;
      const canvas = await html2canvas(document.body, { useCORS: true, scale: 0.5, logging: false });
      setScreenshot(canvas.toDataURL('image/jpeg', 0.5));
    } catch {
      console.error('Screenshot capture failed');
    }
    setCapturing(false);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.securePost('/api/bug-reports', {
        ...form,
        page_url: window.location.href,
        screenshot,
      });
      setSent(true);
      setTimeout(() => { setSent(false); setOpen(false); setForm({ title: '', description: '', severity: 'medium', user_email: '' }); setScreenshot(''); }, 2000);
    } catch {
      alert('Failed to submit report');
    }
    setLoading(false);
  };

  if (chatActive && !open) return null;

  if (!open) {
    return (
      <button data-testid="bug-report-fab" onClick={() => setOpen(true)}
        className="fixed bottom-6 right-6 z-[45] w-12 h-12 rounded-full bg-brand-accent/90 text-white shadow-lg hover:bg-brand-accent transition-all flex items-center justify-center group"
        title="Report a bug">
        <Bug size={20} />
        <span className="absolute right-full mr-3 px-2 py-1 rounded bg-bg-card border border-white/10 text-xs text-txt-secondary whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
          Report a Bug
        </span>
      </button>
    );
  }

  return (
    <div className="fixed bottom-6 right-6 z-50 w-80 max-h-[80vh] overflow-y-auto bg-bg-card border border-white/10 rounded-2xl shadow-2xl" data-testid="bug-report-panel">
      <div className="flex items-center justify-between p-4 border-b border-white/5">
        <div className="flex items-center gap-2">
          <Bug size={16} className="text-brand-accent" />
          <h3 className="font-heading font-semibold text-sm text-txt-primary">Report a Bug</h3>
        </div>
        <button onClick={() => setOpen(false)} className="p-1 rounded-md hover:bg-white/5 text-txt-muted hover:text-white transition-all">
          <X size={16} />
        </button>
      </div>

      {sent ? (
        <div className="p-6 text-center">
          <CheckCircle size={32} className="text-brand-success mx-auto mb-3" />
          <p className="text-sm text-txt-primary font-medium">Bug report submitted!</p>
          <p className="text-xs text-txt-secondary mt-1">Thank you for helping us improve.</p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="p-4 space-y-3">
          <div>
            <label className="text-xs text-txt-muted block mb-1">Title</label>
            <input data-testid="bug-title" type="text" value={form.title} onChange={e => setForm(p => ({...p, title: e.target.value}))}
              placeholder="Brief description" required
              className="w-full bg-bg-primary border border-white/10 rounded-md px-3 py-2 text-sm text-white placeholder:text-txt-muted focus:outline-none focus:border-brand-primary" />
          </div>
          <div>
            <label className="text-xs text-txt-muted block mb-1">Description</label>
            <textarea data-testid="bug-description" value={form.description} onChange={e => setForm(p => ({...p, description: e.target.value}))}
              placeholder="What happened? Steps to reproduce..." rows={3} required
              className="w-full bg-bg-primary border border-white/10 rounded-md px-3 py-2 text-sm text-white placeholder:text-txt-muted focus:outline-none focus:border-brand-primary resize-none" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-txt-muted block mb-1">Severity</label>
              <select data-testid="bug-severity" value={form.severity} onChange={e => setForm(p => ({...p, severity: e.target.value}))}
                className="w-full bg-bg-primary border border-white/10 rounded-md px-3 py-2 text-sm text-white focus:outline-none focus:border-brand-primary">
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="critical">Critical</option>
              </select>
            </div>
            <div>
              <label className="text-xs text-txt-muted block mb-1">Your Email</label>
              <input type="email" value={form.user_email} onChange={e => setForm(p => ({...p, user_email: e.target.value}))}
                placeholder="Optional" data-testid="bug-email"
                className="w-full bg-bg-primary border border-white/10 rounded-md px-3 py-2 text-sm text-white placeholder:text-txt-muted focus:outline-none focus:border-brand-primary" />
            </div>
          </div>

          <div>
            <button type="button" onClick={captureScreenshot} disabled={capturing} data-testid="capture-screenshot-btn"
              className="flex items-center gap-2 text-xs text-txt-secondary hover:text-white transition-colors disabled:opacity-50">
              {capturing ? <Loader2 size={12} className="animate-spin" /> : <Camera size={12} />}
              {screenshot ? 'Retake Screenshot' : 'Capture Screenshot'}
            </button>
            {screenshot && (
              <div className="mt-2 rounded-lg overflow-hidden border border-white/10">
                <img src={screenshot} alt="Screenshot" className="w-full h-auto" data-testid="bug-screenshot-preview" />
              </div>
            )}
          </div>

          <button data-testid="submit-bug-report" type="submit" disabled={loading}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-brand-accent text-white text-sm font-medium hover:opacity-90 transition-all disabled:opacity-50">
            {loading ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
            {loading ? 'Submitting...' : 'Submit Report'}
          </button>
        </form>
      )}
    </div>
  );
}
